import { ChevronDown, ChevronLeft, ChevronRight, ExternalLink, Loader2, Plus, Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { OrderBumpsProvider } from '@/components/config-editor/providers/order-bumps-provider';
import { UpsellsProvider } from '@/components/config-editor/providers/upsells-provider';
import { PriceLineItemsSelector, type PriceLineItem } from '@/components/form/price-line-items-selector';
import {
  OrderBumpSelector,
  PriceSelector,
  ProductSelector,
  VariantSelector,
} from '@/components/resource-pickers';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import localeDataImport from '@/data/locale.json';
import { trpc } from '@/lib/api/trpc';
import { PreviewParamType } from '@/lib/plugin-routing/preview-params';
import type { DetectedRequirements, ResourceRequirement } from '@/lib/plugin-routing/resource-detection';
import { toast } from 'sonner';

// Safe access to locale data with fallback
const localeData = localeDataImport as {
  'primary-dialects': Record<string, string>;
};

type LanguageCode = string;
const AUTO_LOCALE = 'auto';

interface PreviewResourceManagerProps {
  storeId: string;
  instanceId?: string;
  pagePath?: string;
  pageFullUrl?: string;
  paramType: PreviewParamType;
  detectedRequirements?: DetectedRequirements;
  onResourceSelected: (params: Record<string, string>, previewUrl?: string) => void;
  // Optional: Page selector, URL, and screen size switcher to display in the same bar
  pageSelector?: React.ReactNode;
  currentPreviewUrl?: string;
  screenSizeSwitcher?: React.ReactNode;
}

/**
 * Inline Preview Resource Manager
 *
 * Provides a compact, inline interface for selecting or creating preview resources
 * without modals. Shows existing resources and allows creating new ones.
 *
 * 🎯 Enhanced Resource Detection:
 * - Supports detected requirements from plugin manifest (like PreviewRequirementsDialog)
 * - Dynamically renders appropriate resource selectors based on detected requirements
 * - Falls back to paramType-based rendering for backward compatibility
 * - Handles multiple resources per page (e.g., product + variant + price)
 */
export function PreviewResourceManager({
  storeId,
  instanceId,
  pagePath,
  pageFullUrl,
  paramType,
  detectedRequirements,
  onResourceSelected,
  pageSelector,
  currentPreviewUrl,
  screenSizeSwitcher,
}: PreviewResourceManagerProps) {
  const { t } = useTranslation('storefront');
  const [mode, setMode] = useState<'select' | 'create'>('select');
  const [lineItems, setLineItems] = useState<PriceLineItem[]>([]);
  const [selectedLanguage, setSelectedLanguage] = useState<LanguageCode | typeof AUTO_LOCALE>(AUTO_LOCALE);
  const [selectedCurrency, setSelectedCurrency] = useState<string>('EUR');
  const [selectedCheckoutSessionId, setSelectedCheckoutSessionId] = useState<string>('');
  const [selectedOrderId, setSelectedOrderId] = useState<string>('');
  const [selectedOfferId, setSelectedOfferId] = useState<string>('');
  const [resourceValues, setResourceValues] = useState<Record<string, string>>({});
  const [isCreating, setIsCreating] = useState(false);
  const [page, setPage] = useState(0);
  const [isResourcesOpen, setIsResourcesOpen] = useState(true);
  const [orderSearchInput, setOrderSearchInput] = useState('');
  const [orderSearchDebounced, setOrderSearchDebounced] = useState('');
  const itemsPerPage = 5;

  // Debounce order search (400ms)
  useEffect(() => {
    const timer = setTimeout(() => setOrderSearchDebounced(orderSearchInput), 400);
    return () => clearTimeout(timer);
  }, [orderSearchInput]);

  // Get static and non-static resource requirements
  const staticResources = detectedRequirements?.resourceRequirements.filter((req) => req.isStatic) || [];
  const nonStaticResources = detectedRequirements?.resourceRequirements.filter((req) => !req.isStatic) || [];

  // Fetch store data
  const { data: store } = trpc.stores.getById.useQuery({ storeId });

  // Fetch orders for preview (only when paramType is 'order')
  const needsOrder = paramType === 'order' || nonStaticResources.some((req) => req.resourceType === 'order');
  const { data: orders, isFetching: isOrdersFetching } = trpc.orders.getForPreview.useQuery(
    { storeId, limit: 25, search: orderSearchDebounced || undefined },
    { enabled: needsOrder },
  );

  // Fetch offers (only when paramType is 'offer')
  const needsOffer = paramType === 'offer' || nonStaticResources.some((req) => req.resourceType === 'offer');
  const { data: offers } = trpc.offers.list.useQuery({ storeId }, { enabled: needsOffer });

  // Fetch existing checkout sessions for reuse
  const needsCheckoutSession =
    paramType === 'checkoutSession' ||
    nonStaticResources.some((req) => req.resourceType === 'checkoutSession');
  const { data: existingCheckoutSessions, refetch: refetchCheckoutSessions } =
    trpc.checkoutSessions.listForPreview.useQuery({ storeId, limit: 20 }, { enabled: needsCheckoutSession });

  // Create checkout session mutation
  const createCheckoutSession = trpc.checkoutSessions.createPreview.useMutation();

  // Update currency when store changes
  useEffect(() => {
    if (store?.baseCurrency) {
      setSelectedCurrency(store.baseCurrency);
    }
  }, [store]);

  // Reset state and page when paramType changes (page switch)
  useEffect(() => {
    setPage(0);
    setSelectedCheckoutSessionId('');
    setSelectedOrderId('');
    setSelectedOfferId('');
    setResourceValues({});
  }, [paramType]);

  // Auto-select first resource when available
  useEffect(() => {
    if (
      needsCheckoutSession &&
      existingCheckoutSessions &&
      existingCheckoutSessions.length > 0 &&
      !selectedCheckoutSessionId &&
      !resourceValues['checkoutSession']
    ) {
      const firstSession = existingCheckoutSessions[0];
      setSelectedCheckoutSessionId(firstSession.id);
      // Find the checkoutSession requirement to get the correct paramName
      const checkoutReq = nonStaticResources.find((req) => req.resourceType === 'checkoutSession');
      const paramName = checkoutReq?.paramName || 'checkoutToken';
      onResourceSelected({ [paramName]: firstSession.checkoutToken });
    }
  }, [
    existingCheckoutSessions,
    needsCheckoutSession,
    selectedCheckoutSessionId,
    onResourceSelected,
    nonStaticResources,
    resourceValues,
  ]);

  useEffect(() => {
    if (paramType === 'order' && orders && orders.length > 0 && !selectedOrderId) {
      const firstOrderId = orders[0].id;
      setSelectedOrderId(firstOrderId);
      onResourceSelected({ orderId: firstOrderId });
    }
  }, [orders, paramType, selectedOrderId, onResourceSelected]);

  useEffect(() => {
    if (paramType === 'order' && orders && orders.length === 0 && !selectedOrderId) {
      setSelectedOrderId('preview');
      onResourceSelected({ orderId: 'preview' });
    }
  }, [orders, paramType, selectedOrderId, onResourceSelected]);

  // Auto-select preview for detected-requirements order resources when no orders exist
  useEffect(() => {
    if (orders && orders.length === 0 && !selectedOrderId) {
      const orderReq = nonStaticResources.find((req) => req.resourceType === 'order');
      if (orderReq && !resourceValues[orderReq.resourceName]) {
        setSelectedOrderId('preview');
        const paramName = orderReq.paramName || 'orderId';
        const newResourceValues = { ...resourceValues, [orderReq.resourceName]: 'preview' };
        setResourceValues(newResourceValues);
        onResourceSelected({ [paramName]: 'preview' });
      }
    }
  }, [orders, nonStaticResources, selectedOrderId, resourceValues, onResourceSelected]);

  useEffect(() => {
    if (paramType === 'offer' && offers?.offers && offers.offers.length > 0 && !selectedOfferId) {
      const firstOfferId = offers.offers[0].id;
      setSelectedOfferId(firstOfferId);
      onResourceSelected({ offerId: firstOfferId });
    }
  }, [offers, paramType, selectedOfferId, onResourceSelected]);

  const handleCreateResource = async () => {
    setIsCreating(true);

    try {
      if (paramType === 'checkoutSession') {
        if (lineItems.length === 0) {
          alert('Please select at least one product');
          return;
        }

        const fullLocale =
          selectedLanguage === AUTO_LOCALE
            ? undefined
            : localeData['primary-dialects'][
                selectedLanguage as keyof (typeof localeData)['primary-dialects']
              ];

        const lineItemsForApi = lineItems.map((item) => ({
          priceId: item.priceId,
          quantity: item.quantity,
        }));

        const result = await createCheckoutSession.mutateAsync({
          storeId,
          lineItems: lineItemsForApi,
          locale: fullLocale,
          currency: selectedCurrency,
          useCustomDomain: false,
        });

        onResourceSelected({ checkoutToken: result.checkoutToken });

        // Refetch the list to include the new session
        await refetchCheckoutSessions();

        setMode('select');
      }
    } catch (error) {
      console.error('Error creating preview resource:', error);
      alert(error instanceof Error ? error.message : 'Failed to create resource');
    } finally {
      setIsCreating(false);
    }
  };

  const handleSelectCheckoutSession = (sessionId: string) => {
    setSelectedCheckoutSessionId(sessionId);
    const session = existingCheckoutSessions?.find((s) => s.id === sessionId);
    if (session) {
      onResourceSelected({ checkoutToken: session.checkoutToken });
    }
  };

  const handleSelectOrder = (orderId: string) => {
    setSelectedOrderId(orderId);
    onResourceSelected({ orderId });
  };

  const handleSelectOffer = (offerId: string) => {
    setSelectedOfferId(offerId);
    onResourceSelected({ offerId });
  };

  // Create preview session mutation
  const createPreviewSessionMutation = trpc.plugins.createPluginInstancePreviewSession.useMutation();

  // Handle resource selection for detected requirements
  const handleResourceChange = (req: ResourceRequirement, value: string) => {
    const newResourceValues = { ...resourceValues, [req.resourceName]: value };
    setResourceValues(newResourceValues);

    // Build params object with all selected resources using paramName for URL building
    const urlParams: Record<string, string> = {};
    nonStaticResources.forEach((r) => {
      if (newResourceValues[r.resourceName]) {
        // Use paramName for URL building (especially important for path params)
        const paramName = r.paramName || r.name || r.resourceName;
        urlParams[paramName] = newResourceValues[r.resourceName];
      }
    });

    // If we have instanceId and pagePath, create a preview session with static + dynamic resources
    if (instanceId && pagePath && detectedRequirements && pageFullUrl) {
      // Separate static and dynamic resources
      const staticRes: Record<string, string> = {};
      const dynamicRes: Record<string, unknown> = { ...urlParams };

      staticResources.forEach((r) => {
        if (newResourceValues[r.resourceName]) {
          staticRes[r.resourceName] = newResourceValues[r.resourceName];
        }
      });

      // Extract base URL from pageFullUrl
      let instanceBaseUrl = pageFullUrl;
      try {
        const url = new URL(pageFullUrl);
        // Remove path and query params to get base URL
        instanceBaseUrl = `${url.protocol}//${url.host}`;
      } catch {
        // If parsing fails, use as-is
      }

      // Create preview session (non-blocking)
      createPreviewSessionMutation.mutate(
        {
          storeId,
          instanceId,
          pagePath,
          instanceBaseUrl,
          staticResources: staticRes,
          dynamicResources: dynamicRes,
        },
        {
          onSuccess: (result: {
            previewUrl: string;
            funnelSessionId: string;
            token: string;
            customerId: string;
          }) => {
            // Use the preview URL from the session
            onResourceSelected(urlParams, result.previewUrl);
          },
          onError: (error: unknown) => {
            console.error('Error creating preview session:', error);
            toast.error('Failed to create preview session');
            // Fallback to direct URL building
            onResourceSelected(urlParams);
          },
        },
      );
      return;
    }

    // Fallback: use direct URL building (backward compatibility)
    onResourceSelected(urlParams);
  };

  // Helper function to render a resource item
  const renderResourceItem = (req: ResourceRequirement) => (
    <div key={req.resourceName} className="border-border bg-card rounded-lg border p-3 shadow-sm">
      <div className="space-y-2">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1">
            <span className="text-muted-foreground text-[10px] font-medium uppercase tracking-wide">
              {req.label || req.resourceName}
            </span>
            {req.required && <span className="text-[10px] text-red-600">*</span>}
          </div>
          {req.resourceType === 'checkoutSession' && mode === 'select' && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setMode('create')}
              className="h-7 shrink-0 gap-1 px-2.5 text-[10px] font-medium shadow-sm"
            >
              <Plus className="h-3 w-3" />
              {t('configEditor.preview.createNew')}
            </Button>
          )}
        </div>

        {req.resourceType === 'product' && (
          <ProductSelector
            value={resourceValues[req.resourceName] || ''}
            onChange={(value) => handleResourceChange(req, value)}
            storeId={storeId}
            searchable={true}
            placeholder={`Select a ${req.label || 'product'}...`}
          />
        )}

        {req.resourceType === 'variant' && (
          <VariantSelector
            value={resourceValues[req.resourceName] || ''}
            onChange={(value) => handleResourceChange(req, value)}
            storeId={storeId}
          />
        )}

        {req.resourceType === 'price' && (
          <PriceSelector
            value={resourceValues[req.resourceName] || ''}
            onChange={(value) => handleResourceChange(req, value)}
            storeId={storeId}
          />
        )}

        {req.resourceType === 'offer' && (
          <div>
            {offers?.offers && offers.offers.length > 0 && (
              <div className="flex items-center gap-1 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                {offers.offers.slice(0, 10).map((offer: any) => {
                  const isSelected =
                    resourceValues[req.resourceName] === offer.id || selectedOfferId === offer.id;
                  const title = offer.titleTrans?.en || offer.id;

                  return (
                    <button
                      key={offer.id}
                      onClick={() => {
                        setSelectedOfferId(offer.id);
                        handleResourceChange(req, offer.id);
                      }}
                      className={`flex shrink-0 items-center rounded border px-2 py-1 text-[10px] font-medium transition-colors ${
                        isSelected
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-border bg-card text-foreground hover:border-primary/30 hover:bg-muted'
                      }`}
                    >
                      {title}
                    </button>
                  );
                })}
              </div>
            )}

            {(!offers?.offers || offers.offers.length === 0) && (
              <div className="border-border bg-card text-muted-foreground rounded border px-2 py-1.5 text-[10px]">
                {t('configEditor.preview.noOffersFound')}
              </div>
            )}
          </div>
        )}

        {req.resourceType === 'orderBump' && (
          <UpsellsProvider storeId={storeId}>
            <OrderBumpsProvider>
              <OrderBumpSelector
                value={resourceValues[req.resourceName] || ''}
                onChange={(value) => handleResourceChange(req, value)}
                storeId={storeId}
              />
            </OrderBumpsProvider>
          </UpsellsProvider>
        )}

        {req.resourceType === 'order' && (
          <div className="space-y-1.5">
            <div className="relative">
              <div className="absolute left-2 top-1/2 -translate-y-1/2">
                {isOrdersFetching ? (
                  <Loader2 className="text-muted-foreground h-3 w-3 animate-spin" />
                ) : (
                  <Search className="text-muted-foreground h-3 w-3" />
                )}
              </div>
              <Input
                type="text"
                placeholder={t('configEditor.preview.searchOrderPlaceholder')}
                value={orderSearchInput}
                onChange={(e) => setOrderSearchInput(e.target.value)}
                className="h-7 pl-7 text-[10px]"
              />
            </div>
            {orders && orders.length > 0 && (
              <div className="flex items-center gap-1 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                {orders.map((order) => {
                  const isSelected =
                    resourceValues[req.resourceName] === order.id || selectedOrderId === order.id;
                  const amount = order.totalAmount ? (order.totalAmount / 100).toFixed(2) : 'N/A';

                  return (
                    <button
                      key={order.id}
                      onClick={() => {
                        setSelectedOrderId(order.id);
                        handleResourceChange(req, order.id);
                      }}
                      className={`flex shrink-0 items-center gap-1 rounded border px-2 py-1 text-[10px] font-medium transition-colors ${
                        isSelected
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-border bg-card text-foreground hover:border-primary/30 hover:bg-muted'
                      }`}
                    >
                      <span>{order.status}</span>
                      <span className="text-muted-foreground">•</span>
                      <span className="text-muted-foreground text-[9px]">
                        {amount} {order.currency || 'N/A'}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}

            {(!orders || orders.length === 0) && !isOrdersFetching && (
              <div className="border-border bg-card text-muted-foreground rounded border px-2 py-1.5 text-[10px]">
                {orderSearchInput ? t('configEditor.preview.noOrdersMatch') : t('configEditor.preview.noOrdersAvailable')}
              </div>
            )}
          </div>
        )}

        {req.resourceType === 'checkoutSession' && (
          <div>
            {mode === 'select' && (
              <div className="flex items-center gap-1 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                {existingCheckoutSessions && existingCheckoutSessions.length > 0 && (
                  <>
                    {existingCheckoutSessions.slice(0, 10).map((session) => {
                      const isSelected =
                        resourceValues[req.resourceName] === session.checkoutToken ||
                        selectedCheckoutSessionId === session.id;
                      const date = new Date(session.createdAt);
                      const dateStr = date.toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                      });

                      return (
                        <button
                          key={session.id}
                          onClick={() => {
                            setSelectedCheckoutSessionId(session.id);
                            handleResourceChange(req, session.checkoutToken);
                          }}
                          className={`flex shrink-0 items-center gap-1 rounded border px-2 py-1 text-[10px] font-medium transition-colors ${
                            isSelected
                              ? 'border-primary bg-primary/10 text-primary'
                              : 'border-border bg-card text-foreground hover:border-primary/30 hover:bg-muted'
                          }`}
                        >
                          <span>{session.summary}</span>
                          <span className="text-muted-foreground">•</span>
                          <span className="text-muted-foreground text-[9px]">{session.currency}</span>
                          <span className="text-muted-foreground">•</span>
                          <span className="text-muted-foreground text-[9px]">{dateStr}</span>
                        </button>
                      );
                    })}
                  </>
                )}
                {(!existingCheckoutSessions || existingCheckoutSessions.length === 0) && (
                  <div className="border-border bg-card text-muted-foreground rounded border px-2 py-1.5 text-[10px]">
                    {t('configEditor.preview.noCheckoutSessions')}
                  </div>
                )}
              </div>
            )}

            {mode === 'create' && (
              <div className="border-border bg-card space-y-3 rounded-lg border-2 p-3 shadow-sm">
                <div>
                  <PriceLineItemsSelector
                    storeId={storeId}
                    lineItems={lineItems}
                    onLineItemsChange={setLineItems}
                    label={t('configEditor.previewParams.products')}
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">{t('configEditor.previewParams.previewLanguage')}</Label>
                    <Select
                      value={selectedLanguage}
                      onValueChange={(value) =>
                        setSelectedLanguage(value as LanguageCode | typeof AUTO_LOCALE)
                      }
                    >
                      <SelectTrigger className="h-8 text-xs">
                        <SelectValue placeholder={t('configEditor.preview.auto')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={AUTO_LOCALE}>{t('configEditor.preview.auto')}</SelectItem>
                        {localeData?.['primary-dialects'] &&
                          Object.entries(localeData['primary-dialects']).map(([code, fullLocale]) => (
                            <SelectItem key={code} value={code}>
                              {fullLocale}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className="text-xs">{t('configEditor.previewParams.currency')}</Label>
                    <Select value={selectedCurrency} onValueChange={setSelectedCurrency}>
                      <SelectTrigger className="h-8 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {store?.presentmentCurrencies?.map((code: string) => (
                          <SelectItem key={code} value={code}>
                            {code}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    onClick={async () => {
                      if (lineItems.length === 0) {
                        alert('Please select at least one product');
                        return;
                      }

                      setIsCreating(true);
                      try {
                        const fullLocale =
                          selectedLanguage === AUTO_LOCALE
                            ? undefined
                            : localeData['primary-dialects'][
                                selectedLanguage as keyof (typeof localeData)['primary-dialects']
                              ];

                        const lineItemsForApi = lineItems.map((item) => ({
                          priceId: item.priceId,
                          quantity: item.quantity,
                        }));

                        const result = await createCheckoutSession.mutateAsync({
                          storeId,
                          lineItems: lineItemsForApi,
                          locale: fullLocale,
                          currency: selectedCurrency,
                          useCustomDomain: false,
                        });

                        handleResourceChange(req, result.checkoutToken);
                        await refetchCheckoutSessions();
                        setMode('select');
                      } catch (error) {
                        console.error('Error creating checkout session:', error);
                        alert(error instanceof Error ? error.message : 'Failed to create checkout session');
                      } finally {
                        setIsCreating(false);
                      }
                    }}
                    disabled={lineItems.length === 0 || isCreating}
                    className="h-8 flex-1 text-xs font-medium shadow-sm"
                  >
                    {isCreating && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
                    {t('configEditor.preview.createAndPreview')}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setMode('select')}
                    className="h-8 px-3 text-xs"
                  >
                    {t('configEditor.previewParams.cancel')}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );

  // Render based on detected requirements or fallback to paramType
  // If we have detected requirements with multiple resources, show advanced UI
  if (detectedRequirements && (nonStaticResources.length > 0 || staticResources.length > 0)) {
    // Calculate total resources to determine grid layout
    const totalResources = staticResources.length + nonStaticResources.length;
    const gridColsClass = totalResources === 1 ? 'grid-cols-1' : 'grid-cols-2';

    // Combine all resources into a single array for unified grid layout
    const allResources = [...staticResources, ...nonStaticResources];

    return (
      <div className="space-y-1">
        {/* Top row: Page selector and screen size switcher */}
        <div className="border-border bg-card border-b px-4 py-2.5 shadow-sm">
          <div className="relative flex items-center">
            <div className="flex-1">{pageSelector}</div>
            <div className="absolute left-1/2 -translate-x-1/2">{screenSizeSwitcher}</div>
            <div className="flex flex-1 justify-end">
              {currentPreviewUrl && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => window.open(currentPreviewUrl, '_blank')}
                  className="h-7 w-7 p-0"
                  title={t('configEditor.preview.openInNewTab')}
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Combined Resources Grid - Static and Non-Static together */}
        {allResources.length > 0 && (
          <Collapsible open={isResourcesOpen} onOpenChange={setIsResourcesOpen}>
            <CollapsibleTrigger className="w-full">
              <div className="border-border bg-card flex w-full items-center justify-between border-b px-4 py-2.5">
                <span className="font-medium">{t('stepEditor.designResources')}</span>
                <ChevronDown
                  className={`h-4 w-4 transition-transform ${isResourcesOpen ? 'rotate-180' : ''}`}
                />
              </div>
            </CollapsibleTrigger>
            <CollapsibleContent className="bg-card max-h-96 overflow-y-auto border border-b px-4 py-2">
              <div className={`grid gap-2 ${gridColsClass}`}>
                {allResources.map((req) => renderResourceItem(req))}
              </div>
            </CollapsibleContent>
          </Collapsible>
        )}
      </div>
    );
  }

  // Fallback to original paramType-based rendering for backward compatibility
  if (paramType === 'none') {
    // If we have page selector, show the bar even without resources
    if (pageSelector) {
      return (
        <div className="border-border bg-card border-b px-4 py-2.5 shadow-sm">
          <div className="relative flex items-center">
            <div className="flex-1">{pageSelector}</div>
            {screenSizeSwitcher && (
              <div className="absolute left-1/2 -translate-x-1/2">{screenSizeSwitcher}</div>
            )}
            <div className="flex flex-1 justify-end">
              {currentPreviewUrl && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => window.open(currentPreviewUrl, '_blank')}
                  className="h-7 w-7 p-0"
                  title={t('configEditor.preview.openInNewTab')}
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          </div>
        </div>
      );
    }
    return null;
  }

  if (paramType === 'checkoutSession') {
    return (
      <div className="space-y-1">
        {/* Top row: Page selector and screen size switcher */}
        <div className="border-border bg-card border-b px-4 py-2.5 shadow-sm">
          <div className="relative flex items-center">
            <div className="flex-1">{pageSelector}</div>
            <div className="absolute left-1/2 -translate-x-1/2">{screenSizeSwitcher}</div>
            <div className="flex flex-1 items-center justify-end gap-1">
              {currentPreviewUrl && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => window.open(currentPreviewUrl, '_blank')}
                  className="h-7 w-7 p-0"
                  title={t('configEditor.preview.openInNewTab')}
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                </Button>
              )}
              {mode === 'create' && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setMode('select')}
                  className="h-8 px-3 text-xs"
                >
                  {t('configEditor.previewParams.cancel')}
                </Button>
              )}
            </div>
          </div>
        </div>

        <div className="px-4 pt-2">
          {mode === 'select' &&
            existingCheckoutSessions &&
            existingCheckoutSessions.length > 0 &&
            (() => {
              const startIdx = page * itemsPerPage;
              const endIdx = startIdx + itemsPerPage;
              const paginatedSessions = existingCheckoutSessions.slice(startIdx, endIdx);
              const totalPages = Math.ceil(existingCheckoutSessions.length / itemsPerPage);
              const hasPrev = page > 0;
              const hasNext = page < totalPages - 1;

              return (
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                    disabled={!hasPrev}
                    className="border-border bg-card hover:bg-muted text-foreground flex h-8 w-8 items-center justify-center rounded border transition-colors disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <ChevronLeft className="h-3.5 w-3.5" />
                  </button>

                  <div className="flex flex-1 gap-1 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setMode('create')}
                      className="h-8 shrink-0 gap-1.5 px-3 text-xs font-medium shadow-sm"
                    >
                      <Plus className="h-3.5 w-3.5" />
                      {t('configEditor.preview.createNew')}
                    </Button>
                    {paginatedSessions.map((session) => {
                      const isSelected = selectedCheckoutSessionId === session.id;
                      const date = new Date(session.createdAt);
                      const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

                      return (
                        <button
                          key={session.id}
                          onClick={() => handleSelectCheckoutSession(session.id)}
                          className={`flex shrink-0 items-center gap-1 rounded border px-2 py-1 text-[10px] font-medium transition-colors ${
                            isSelected
                              ? 'border-primary bg-primary/10 text-primary'
                              : 'border-border bg-card text-foreground hover:border-primary/30 hover:bg-muted'
                          }`}
                        >
                          <span>{session.summary}</span>
                          <span className="text-muted-foreground">•</span>
                          <span className="text-muted-foreground text-[9px]">{session.currency}</span>
                          <span className="text-muted-foreground">•</span>
                          <span className="text-muted-foreground text-[9px]">{dateStr}</span>
                        </button>
                      );
                    })}
                  </div>

                  <button
                    onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                    disabled={!hasNext}
                    className="border-border bg-card hover:bg-muted text-foreground flex h-8 w-8 items-center justify-center rounded border transition-colors disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <ChevronRight className="h-3.5 w-3.5" />
                  </button>
                </div>
              );
            })()}

          {mode === 'select' && (!existingCheckoutSessions || existingCheckoutSessions.length === 0) && (
            <div className="flex items-center gap-1">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setMode('create')}
                className="h-8 shrink-0 gap-1.5 px-3 text-xs font-medium shadow-sm"
              >
                <Plus className="h-3.5 w-3.5" />
                {t('configEditor.preview.createNew')}
              </Button>
              <div className="border-border bg-card text-muted-foreground rounded border px-2 py-1.5 text-[10px]">
                {t('configEditor.preview.noCheckoutSessions')}
              </div>
            </div>
          )}

          {mode === 'create' && (
            <div className="border-border bg-card space-y-3 rounded-lg border-2 p-3 shadow-sm">
              <div>
                <PriceLineItemsSelector
                  storeId={storeId}
                  lineItems={lineItems}
                  onLineItemsChange={setLineItems}
                  label={t('configEditor.previewParams.products')}
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">{t('configEditor.previewParams.previewLanguage')}</Label>
                  <Select
                    value={selectedLanguage}
                    onValueChange={(value) => setSelectedLanguage(value as LanguageCode | typeof AUTO_LOCALE)}
                  >
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue placeholder={t('configEditor.preview.auto')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={AUTO_LOCALE}>{t('configEditor.preview.auto')}</SelectItem>
                      {localeData?.['primary-dialects'] &&
                        Object.entries(localeData['primary-dialects']).map(([code, fullLocale]) => (
                          <SelectItem key={code} value={code}>
                            {fullLocale}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-xs">{t('configEditor.previewParams.currency')}</Label>
                  <Select value={selectedCurrency} onValueChange={setSelectedCurrency}>
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {store?.presentmentCurrencies?.map((code: string) => (
                        <SelectItem key={code} value={code}>
                          {code}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <Button
                size="sm"
                onClick={handleCreateResource}
                disabled={lineItems.length === 0 || isCreating}
                className="h-8 w-full text-xs font-medium shadow-sm"
              >
                {isCreating && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
                {t('configEditor.preview.createAndPreview')}
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (paramType === 'order') {
    return (
      <div className="border-border bg-muted/30 border-b px-4">
        {/* Top row: Page selector and screen size switcher */}
        <div className="border-border bg-card border-b px-0 py-2.5 shadow-sm">
          <div className="relative flex items-center px-4">
            <div className="flex-1">{pageSelector}</div>
            <div className="absolute left-1/2 -translate-x-1/2">{screenSizeSwitcher}</div>
            <div className="flex flex-1 justify-end">
              {currentPreviewUrl && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => window.open(currentPreviewUrl, '_blank')}
                  className="h-7 w-7 p-0"
                  title={t('configEditor.preview.openInNewTab')}
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          </div>
        </div>
        <div className="pt-2">
          {orders && orders.length > 0 ? (
            (() => {
              const startIdx = page * itemsPerPage;
              const endIdx = startIdx + itemsPerPage;
              const paginatedOrders = orders.slice(startIdx, endIdx);
              const totalPages = Math.ceil(orders.length / itemsPerPage);
              const hasPrev = page > 0;
              const hasNext = page < totalPages - 1;

              return (
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                    disabled={!hasPrev}
                    className="border-border bg-card hover:bg-muted text-foreground flex h-8 w-8 items-center justify-center rounded border transition-colors disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <ChevronLeft className="h-3.5 w-3.5" />
                  </button>

                  <div className="flex flex-1 gap-1 overflow-hidden">
                    {paginatedOrders.map((order) => {
                      const isSelected = selectedOrderId === order.id;
                      const amount = order.totalAmount ? (order.totalAmount / 100).toFixed(2) : 'N/A';

                      return (
                        <button
                          key={order.id}
                          onClick={() => handleSelectOrder(order.id)}
                          className={`flex shrink-0 items-center gap-1 rounded border px-2 py-1 text-[10px] font-medium transition-colors ${
                            isSelected
                              ? 'border-primary bg-primary/10 text-primary'
                              : 'border-border bg-card text-foreground hover:border-primary/30 hover:bg-muted'
                          }`}
                        >
                          <span>{order.status}</span>
                          <span className="text-muted-foreground">•</span>
                          <span className="text-muted-foreground text-[9px]">
                            {amount} {order.currency || 'N/A'}
                          </span>
                        </button>
                      );
                    })}
                  </div>

                  <button
                    onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                    disabled={!hasNext}
                    className="border-border bg-card hover:bg-muted text-foreground flex h-8 w-8 items-center justify-center rounded border transition-colors disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <ChevronRight className="h-3.5 w-3.5" />
                  </button>
                </div>
              );
            })()
          ) : (
            <div className="border-border bg-card text-muted-foreground rounded border p-2 text-[10px]">
              {t('configEditor.preview.noOrdersAvailable')}
            </div>
          )}
        </div>
      </div>
    );
  }

  if (paramType === 'offer') {
    return (
      <div className="border-border bg-muted/30 border-b px-4">
        {/* Top row: Page selector and screen size switcher */}
        <div className="border-border bg-card border-b px-0 py-2.5 shadow-sm">
          <div className="relative flex items-center px-4">
            <div className="flex-1">{pageSelector}</div>
            <div className="absolute left-1/2 -translate-x-1/2">{screenSizeSwitcher}</div>
            <div className="flex flex-1 justify-end">
              {currentPreviewUrl && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => window.open(currentPreviewUrl, '_blank')}
                  className="h-7 w-7 p-0"
                  title={t('configEditor.preview.openInNewTab')}
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          </div>
        </div>
        <div className="pt-2">
          {offers?.offers && offers.offers.length > 0 ? (
            (() => {
              const allOffers = offers.offers;
              const startIdx = page * itemsPerPage;
              const endIdx = startIdx + itemsPerPage;
              const paginatedOffers = allOffers.slice(startIdx, endIdx);
              const totalPages = Math.ceil(allOffers.length / itemsPerPage);
              const hasPrev = page > 0;
              const hasNext = page < totalPages - 1;

              return (
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                    disabled={!hasPrev}
                    className="border-border bg-card hover:bg-muted text-foreground flex h-8 w-8 items-center justify-center rounded border transition-colors disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <ChevronLeft className="h-3.5 w-3.5" />
                  </button>

                  <div className="flex flex-1 gap-1 overflow-hidden">
                    {paginatedOffers.map((offer: any) => {
                      const isSelected = selectedOfferId === offer.id;
                      const title = offer.titleTrans?.en || offer.id;

                      return (
                        <button
                          key={offer.id}
                          onClick={() => handleSelectOffer(offer.id)}
                          className={`flex shrink-0 items-center rounded border px-2 py-1 text-[10px] font-medium transition-colors ${
                            isSelected
                              ? 'border-primary bg-primary/10 text-primary'
                              : 'border-border bg-card text-foreground hover:border-primary/30 hover:bg-muted'
                          }`}
                        >
                          {title}
                        </button>
                      );
                    })}
                  </div>

                  <button
                    onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                    disabled={!hasNext}
                    className="border-border bg-card hover:bg-muted text-foreground flex h-8 w-8 items-center justify-center rounded border transition-colors disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <ChevronRight className="h-3.5 w-3.5" />
                  </button>
                </div>
              );
            })()
          ) : (
            <div className="border-border bg-card text-muted-foreground rounded border p-2 text-[10px]">
              {t('configEditor.preview.noOffersFound')}
            </div>
          )}
        </div>
      </div>
    );
  }

  return null;
}
